import React from "react";
import _ from "lodash";
import { GamAccountAPI, UnitAPI } from "apis";
import UnitStatus from "components/common/UnitStatus";
import UnitMode from "components/common/UnitMode";
import NetworkTextDisplay from "components/common/NetworkTextDisplay";
import VaultAccountViewByNetworkModal from "components/vault/VaultAccountViewByNetworkModal";
import SyncVerifyNetworkButton from "components/ops-mgmt/account/network-management/SyncVerifyNetworkButton";
import NetworkEnsureResourcesButton from "components/ops-mgmt/account/NetworkEnsureResources";
import UpdateAdServingCostModal from "components/ops-mgmt/account/network-management/UpdateAdServingCostModal";
import UpdateNetworkBillableTypes from "components/ops-mgmt/account/network-management/UpdateNetworkBillableTypes";
import GoToPageSelector from "components/common/GoToPageSelector";
import { buttonActionClassGray } from "helpers/StyleClass";
import { MESSAGE } from "constants/Message";
import PromptModal from "components/common/PromptModal";

class OnboardSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      units: [],

      isAccountModalOpen: false,
      isUpdateAdServingCostModalOpen: false,

      isLoading: false,
      isPromptModalOpen: false,
      promptHeader: null,
      promptMessage: null,
      promptAction: null,
      promptMsg: [],
    };

    this.handleSync = this.handleSync.bind(this);
    this.handleUpdateNetworkStatus = this.handleUpdateNetworkStatus.bind(this);
    this.getNetworkActionPaths = this.getNetworkActionPaths.bind(this);
    this.handleUpdateNetworkProps = this.handleUpdateNetworkProps.bind(this);
  }

  async componentDidMount() {
    const { selectedNetwork, selectedYieldSet } = this.props;

    this.props.handleLoading(true);
    if (selectedNetwork && selectedYieldSet) {
      try {
        const { units } = await UnitAPI.searchUnits({
          networkId: selectedNetwork.networkId,
          yieldSetId: selectedYieldSet.yieldSetId,
        });

        this.setState({ units });
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    }
    this.props.handleLoading(false);
  }

  // Action: Sync GAM Account
  async handleSync({ accountId, name }, isPrompt = true) {
    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptMsg: [],
        promptHeader: "Sync GAM Account",
        promptMessage: (
          <>
            Are you sure you want to sync this GAM account? <br />
            <span className="font-semibold text-blue-600">
              {accountId} {name}
            </span>
          </>
        ),
        promptAction: () => this.handleSync({ accountId }, false),
      });
      return;
    }

    this.setState({ isLoading: true, promptMsg: [] });
    try {
      const result = await GamAccountAPI.syncGamAccount({ accountId });

      if (result) {
        this.props.handleChangeAccount(result);
        this.setState({ promptMsg: ["Sync Success", MESSAGE.SUCCESS] });
      }
    } catch (error) {
      this.setState({ promptMsg: [error, MESSAGE.ERROR] });
    }

    this.setState({ isLoading: false });
  }

  handleUpdateNetworkStatus({ status }) {
    const { selectedNetwork } = this.props;

    this.props.handleChangeNetwork({
      ...selectedNetwork,
      status,
    });
  }

  handleUpdateNetworkProps({
    billableDemandTypes,
    billableRequestTypes,
    billableInventoryFormats,
  }) {
    const { selectedNetwork } = this.props;

    this.props.handleChangeNetwork({
      ...selectedNetwork,
      billableDemandTypes,
      billableRequestTypes,
      billableInventoryFormats,
    });
  }

  getNetworkActionPaths(networkId) {
    return [
      {
        name: "UPR Segmentation",
        path: `/ops-mgmt/account/network/${networkId}/upr-segmentation`,
      },
      {
        name: "Custom UPR",
        path: `/ops-mgmt/account/network/${networkId}/upr-setting`,
      },
      {
        name: "CST Config",
        path: `/ops-mgmt/account/network/${networkId}/cst-config`,
      },
      {
        name: "Virtual Placements",
        path: `/ops-mgmt/account/network/${networkId}/virtual-placement/list`,
      },
      {
        name: "App Configuration",
        path: `/ops-mgmt/account/network/${networkId}/ac-raw-config`,
      },
    ];
  }

  render() {
    const { selectedAccount, selectedNetwork, selectedYieldSet } = this.props;
    const {
      units,
      isAccountModalOpen,
      isUpdateAdServingCostModalOpen,
      isPromptModalOpen,
      isLoading,
      promptAction,
      promptHeader,
      promptMessage,
      promptMsg,
    } = this.state;

    return (
      <div className="mx-auto mt-6 w-4/5">
        <div className="mb-4 flex gap-4">
          <div className="rounded flex w-1/3 flex-col bg-gray-100 px-4 py-2">
            <div className="mb-2 text-lg font-semibold text-blue-800">
              GAM Account
            </div>

            {["accountId", "name", "status"].map((key) => (
              <Item
                key={key}
                label={_.startCase(key)}
                value={selectedAccount[key] || "N/A"}
              />
            ))}

            <div className="my-2 flex justify-start gap-2">
              <button
                className={buttonActionClassGray}
                onClick={() => this.handleSync(selectedAccount)}
              >
                Sync GAM Account
              </button>
            </div>
          </div>
          <div className="rounded w-2/3 bg-gray-100 px-4 py-2">
            <div className="mb-2 text-lg font-semibold text-blue-800">
              Network
            </div>
            {["networkId"].map((key) => (
              <Item
                key={key}
                label={_.startCase(key)}
                value={selectedNetwork[key] || "N/A"}
              />
            ))}
            <Item
              label={"Name"}
              value={
                <NetworkTextDisplay
                  network={selectedNetwork}
                  hideIndicator={true}
                  hideId={true}
                ></NetworkTextDisplay>
              }
            />
            {["product"].map((key) => (
              <Item
                key={key}
                label={_.startCase(key)}
                value={selectedNetwork[key] || "N/A"}
              />
            ))}
            {[
              "billableDemandTypes",
              "billableRequestTypes",
              "billableInventoryFormats",
            ].map((key) => (
              <Item
                width="w-56"
                key={key}
                label={`${_.startCase(key)} (${
                  (selectedNetwork[key] || []).length
                })`}
                value={
                  <div className="flex flex-wrap gap-x-2 gap-y-1 pt-1">
                    {selectedNetwork[key]
                      ? selectedNetwork[key].map((d) => {
                          return (
                            <div
                              key={d}
                              className="rounded bg-gray-300 px-2 text-xs text-gray-800"
                            >
                              {d}
                            </div>
                          );
                        })
                      : "--"}
                  </div>
                }
              />
            ))}

            <div className="my-2 flex justify-start gap-2">
              <SyncVerifyNetworkButton
                networkId={selectedNetwork.networkId}
                name={selectedNetwork.name}
                status={selectedNetwork.status}
                updateNetworkStatus={this.handleUpdateNetworkStatus}
                customClassName={"block"}
                customActionClass={buttonActionClassGray}
              ></SyncVerifyNetworkButton>
              <NetworkEnsureResourcesButton
                networkId={selectedNetwork.networkId}
                name={selectedNetwork.name}
                customActionClass={buttonActionClassGray}
              ></NetworkEnsureResourcesButton>
              <UpdateNetworkBillableTypes
                networkId={selectedNetwork.networkId}
                name={selectedNetwork.name}
                billableDemandTypes={selectedNetwork.billableDemandTypes}
                billableRequestTypes={selectedNetwork.billableRequestTypes}
                billableInventoryFormats={
                  selectedNetwork.billableInventoryFormats
                }
                handleUpdateNetwork={this.handleUpdateNetworkProps}
              ></UpdateNetworkBillableTypes>
            </div>
          </div>
        </div>

        <div className="rounded mb-4 bg-gray-100 px-4 py-2">
          <div className="mb-2 text-lg font-semibold text-blue-800">
            Network Configs
          </div>

          <div className="my-2 flex justify-start gap-2">
            <button
              className={buttonActionClassGray}
              onClick={() => this.setState({ isAccountModalOpen: true })}
            >
              GAM Account
            </button>
            <button
              className={buttonActionClassGray}
              onClick={() => {
                window.open(
                  `${window.location.origin}/#/ops-mgmt/account/network/${selectedNetwork.networkId}/features`
                );
              }}
            >
              Network Feature
            </button>
            <button
              className={buttonActionClassGray}
              onClick={() => {
                window.open(
                  `${window.location.origin}/#/ops-mgmt/account/network/${selectedNetwork.networkId}/code-snippet`
                );
              }}
            >
              Code Snippet
            </button>
            <button
              className={buttonActionClassGray}
              onClick={() =>
                this.setState({ isUpdateAdServingCostModalOpen: true })
              }
            >
              Update Ad Serving Cost
            </button>

            <GoToPageSelector
              goToPaths={this.getNetworkActionPaths(selectedNetwork.networkId)}
              customClassName={buttonActionClassGray}
            ></GoToPageSelector>
          </div>
        </div>

        <div className="rounded bg-gray-100 px-4 py-2">
          <div className="mb-2 text-lg font-semibold text-blue-800">
            Yield Set
          </div>
          {selectedYieldSet ? (
            ["yieldSetId", "name", "status"].map((key) => (
              <Item
                key={key}
                label={_.startCase(key)}
                value={selectedYieldSet[key] || "N/A"}
              />
            ))
          ) : (
            <div className="mb-1 font-semibold text-gray-600">--</div>
          )}
          <div className="mb-1 w-24 text-gray-600">
            Ad Units ({units.length})
          </div>
          <div className="flex flex-wrap gap-2 font-sans font-semibold">
            {units.map((unit) => (
              <div key={unit.adUnitId} className="border rounded p-1">
                <span className="text-gray-800">{unit.adUnitId}</span>
                <span className="mx-1 text-gray-700">{unit.name}</span>
                <UnitStatus unit={unit}></UnitStatus>
                <UnitMode unit={unit} version="v2"></UnitMode>
              </div>
            ))}
          </div>
        </div>

        {isAccountModalOpen && (
          <VaultAccountViewByNetworkModal
            network={selectedNetwork}
            isOpenModal={isAccountModalOpen}
            handleClose={() => this.setState({ isAccountModalOpen: false })}
          ></VaultAccountViewByNetworkModal>
        )}

        {isPromptModalOpen && (
          <PromptModal
            isLoading={isLoading}
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            msg={promptMsg}
            handleCancel={() =>
              this.setState({ isPromptModalOpen: false, promptMsg: [] })
            }
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}

        {isUpdateAdServingCostModalOpen && (
          <UpdateAdServingCostModal
            network={selectedNetwork}
            handleClose={() =>
              this.setState({ isUpdateAdServingCostModalOpen: false })
            }
          ></UpdateAdServingCostModal>
        )}
      </div>
    );
  }
}

class Item extends React.Component {
  render() {
    const { label, value, width = "" } = this.props;

    return (
      <div className="mb-1 flex gap-1">
        <span className={`${width === "" ? "w-24" : width} text-gray-600`}>
          {label}:
        </span>
        <span className="flex-1 font-semibold text-gray-800">{value}</span>
      </div>
    );
  }
}

export default OnboardSummary;
